import { useCallback, useMemo, useState, type Dispatch, type SetStateAction } from "react";
import { api } from "../../../lib/api";
import { getAuthToken } from "../../../lib/auth";
import { toast } from "../../../lib/ui";
import { useLatest } from "../../../hooks/useLatest";

type UseQuickChannelOptions = {
  currentVoidId: string;
  isSpaceOwner: boolean;
  socket: any;
  // channels state stays in the composition layer (5.1c deferred to Phase 7);
  // handleQuickChannelCreate optimistically appends the new channel via this setter.
  setChannels: Dispatch<SetStateAction<any[]>>;
  // moveChannelToFolder is a parent function declaration (hoisted), passed in so
  // quickAddChannelToFolder's body moves verbatim.
  moveChannelToFolder: (channelId: string, folderId: string | null) => Promise<void>;
};

/**
 * Quick channel-creation surface (step 5.1e — last clean slice of the channels
 * cluster): the QuickChannel modal flags (`quickChannelOpen`/`quickChannelBusy`),
 * the channel-type option list, and the three create actions —
 * `quickAddChannel` (owner-gated modal open), `quickAddChannelToFolder` (create
 * then move into a folder), and `handleQuickChannelCreate` (create with a type).
 *
 * All three create via POST /channels and let `onChannelList` reconcile;
 * handleQuickChannelCreate also does an optimistic `setChannels` append.
 * `channels` itself + `onChannelList` stay in the parent (5.1c deferred), so the
 * setter and `moveChannelToFolder` come in as inputs.
 */
export function useQuickChannel({ currentVoidId, isSpaceOwner, socket, setChannels, moveChannelToFolder }: UseQuickChannelOptions) {
  const [quickChannelOpen, setQuickChannelOpen] = useState(false);
  const [quickChannelBusy, setQuickChannelBusy] = useState(false);
  // The parent re-creates moveChannelToFolder every render (it reads parent state); call the newest one.
  const moveChannelToFolderRef = useLatest(moveChannelToFolder);
  const channelTypeOptions = useMemo(
    () => [
      { id: "text", label: "Text" },
      { id: "voice", label: "Voice" },
      { id: "announcement", label: "Announcement" },
      { id: "kanban", label: "Kanban" },
      { id: "form", label: "Questions" },
      { id: "habit", label: "Task" },
      { id: "gallery", label: "Gallery" },
      { id: "kitchen", label: "Kitchen" },
      { id: "calendar", label: "Calendar" },
      { id: "notes", label: "Notes" },
      { id: "challenge", label: "Challenge" },
    ],
    []
  );

  const quickAddChannel = useCallback(() => {
    if (!currentVoidId) {
      toast('Choose a space first', 'error');
      return;
    }
    if (!isSpaceOwner) {
      toast('Only space owners can add channels', 'error');
      return;
    }
    setQuickChannelOpen(true);
  }, [currentVoidId, isSpaceOwner]);

  const quickAddChannelToFolder = useCallback(async (folderId: string, name: string) => {
    if (!currentVoidId) return;
    setQuickChannelBusy(true);
    try {
      const tok = getAuthToken() || '';
      const res = await api.postAuth('/channels', { spaceId: currentVoidId, name: name.trim(), type: 'text' }, tok);
      const cidRaw = String(res?.id ?? '');
      if (cidRaw) {
        await moveChannelToFolderRef.current(cidRaw, folderId);
      }
      socket.emit("channel:list", { voidId: currentVoidId });
    } catch (e:any) {
      toast(e?.message || 'Failed to create channel', 'error');
    } finally {
      setQuickChannelBusy(false);
    }
  }, [currentVoidId, socket, moveChannelToFolderRef]);

  const handleQuickChannelCreate = useCallback(async (name: string, channelType: string) => {
    if (!currentVoidId || !name.trim()) return;
    setQuickChannelBusy(true);
    try {
      const tok = getAuthToken() || '';
      const type = channelType || 'text';
      const res = await api.postAuth('/channels', { spaceId: currentVoidId, name: name.trim(), type }, tok);
      const cidRaw = String(res?.id ?? '');
      setChannels((prev) => {
        if (!cidRaw) return prev;
        const exists = prev.some((c) => c.id === cidRaw || (c.name === name && c.voidId === currentVoidId));
        if (exists) return prev;
        return [...prev, { id: cidRaw, name: name.trim(), type: (res as any)?.type || type, voidId: currentVoidId, allowedRoles: [] }];
      });
      socket.emit('channel:list', { voidId: currentVoidId });
      toast('Channel created', 'success');
      setQuickChannelOpen(false);
    } catch (e: any) {
      toast(e?.message || 'Failed to create channel', 'error');
    } finally {
      setQuickChannelBusy(false);
    }
  }, [currentVoidId, setChannels, socket]);

  return {
    quickChannelOpen,
    setQuickChannelOpen,
    quickChannelBusy,
    channelTypeOptions,
    quickAddChannel,
    quickAddChannelToFolder,
    handleQuickChannelCreate,
  };
}
